/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	AreaOfInterest,
	AreaOfInterestCreate,
	AreaOfInterestUpdate,
	CreateAOI400,
	CreateAOI500,
	DeleteAOI400,
	DeleteAOI404,
	DeleteAOI500,
	GetAOI400,
	GetAOI404,
	GetAOI500,
	ListAOIs400,
	ListAOIs500,
	UpdateAOI400,
	UpdateAOI404,
	UpdateAOI500,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getGetAOIUrl = (aoiId: string) => {
	return `/aois/${aoiId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Get a single Area of Interest by ID
 */
export const getAOI = async (
	aoiId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	return customFetch<AreaOfInterest>(getGetAOIUrl(aoiId), {
		...options,
		method: "GET",
	});
};

export const getGetAOIQueryKey = (aoiId: string) => {
	return ["aois", aoiId] as const;
};

export const getGetAOIQueryOptions = <
	TData = Awaited<ReturnType<typeof getAOI>>,
	TError = GetAOI400 | void | GetAOI404 | GetAOI500,
>(
	aoiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAOI>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getGetAOIQueryKey(aoiId);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof getAOI>>> = ({
		signal,
	}) => getAOI(aoiId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: aoiId !== null && aoiId !== undefined,
		...queryOptions,
	} as UseQueryOptions<Awaited<ReturnType<typeof getAOI>>, TError, TData> & {
		queryKey: DataTag<QueryKey, TData, TError>;
	};
};

export type GetAOIQueryResult = NonNullable<Awaited<ReturnType<typeof getAOI>>>;
export type GetAOIQueryError = GetAOI400 | void | GetAOI404 | GetAOI500;

export function useGetAOI<
	TData = Awaited<ReturnType<typeof getAOI>>,
	TError = GetAOI400 | void | GetAOI404 | GetAOI500,
>(
	aoiId: string,
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAOI>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAOI>>,
					TError,
					Awaited<ReturnType<typeof getAOI>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAOI<
	TData = Awaited<ReturnType<typeof getAOI>>,
	TError = GetAOI400 | void | GetAOI404 | GetAOI500,
>(
	aoiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAOI>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getAOI>>,
					TError,
					Awaited<ReturnType<typeof getAOI>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAOI<
	TData = Awaited<ReturnType<typeof getAOI>>,
	TError = GetAOI400 | void | GetAOI404 | GetAOI500,
>(
	aoiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAOI>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a single Area of Interest by ID
 */

export function useGetAOI<
	TData = Awaited<ReturnType<typeof getAOI>>,
	TError = GetAOI400 | void | GetAOI404 | GetAOI500,
>(
	aoiId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getAOI>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetAOIQueryOptions(aoiId, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get a single Area of Interest by ID
 */
export const useSetGetAOIQueryData = () => {
	const queryClient = useQueryClient();
	return (
		aoiId: string,
		updater:
			| Awaited<ReturnType<typeof getAOI>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getAOI>> | undefined,
			  ) => Awaited<ReturnType<typeof getAOI>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getAOI>>>(
			{ exact: $exactMatch, queryKey: getGetAOIQueryKey(aoiId) },
			updater,
		);
	};
};

/**
 * @summary Get a single Area of Interest by ID
 */
export const useGetGetAOIQueryData = () => {
	const queryClient = useQueryClient();
	return (aoiId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getAOI>>>(
			getGetAOIQueryKey(aoiId),
		);
};

export const getUpdateAOIUrl = (aoiId: string) => {
	return `/aois/${aoiId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Update an existing Area of Interest
 */
export const updateAOI = async (
	aoiId: string,
	areaOfInterestUpdate: AreaOfInterestUpdate,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<AreaOfInterest>(getUpdateAOIUrl(aoiId), {
		...options,
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(areaOfInterestUpdate),
	});
};

export const getUpdateAOIMutationKey = () => ["updateAOI"] as const;

export const getUpdateAOIMutationOptions = <
	TError = UpdateAOI400 | void | UpdateAOI404 | UpdateAOI500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof updateAOI>>,
		TError,
		UpdateAOIMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof updateAOI>>,
	TError,
	UpdateAOIMutationVariables,
	TContext
> => {
	const mutationKey = getUpdateAOIMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof updateAOI>>,
		UpdateAOIMutationVariables
	> = (props) => {
		const { aoiId, data } = props ?? {};

		return updateAOI(aoiId, data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type UpdateAOIMutationResult = NonNullable<
	Awaited<ReturnType<typeof updateAOI>>
>;
export type UpdateAOIMutationBody = AreaOfInterestUpdate;
export type UpdateAOIMutationError =
	| UpdateAOI400
	| void
	| UpdateAOI404
	| UpdateAOI500;
export type UpdateAOIMutationVariables = {
	aoiId: string;
	data: AreaOfInterestUpdate;
};

/**
 * @summary Update an existing Area of Interest
 */
export const useUpdateAOI = <
	TError = UpdateAOI400 | void | UpdateAOI404 | UpdateAOI500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof updateAOI>>,
			TError,
			UpdateAOIMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof updateAOI>>,
	TError,
	UpdateAOIMutationVariables,
	TContext
> => {
	return useMutation(getUpdateAOIMutationOptions(options), queryClient);
};
export const getDeleteAOIUrl = (aoiId: string) => {
	return `/aois/${aoiId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Delete an existing Area of Interest
 */
export const deleteAOI = async (
	aoiId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	return customFetch<AreaOfInterest>(getDeleteAOIUrl(aoiId), {
		...options,
		method: "DELETE",
	});
};

export const getDeleteAOIMutationKey = () => ["deleteAOI"] as const;

export const getDeleteAOIMutationOptions = <
	TError = DeleteAOI400 | void | DeleteAOI404 | DeleteAOI500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteAOI>>,
		TError,
		DeleteAOIMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof deleteAOI>>,
	TError,
	DeleteAOIMutationVariables,
	TContext
> => {
	const mutationKey = getDeleteAOIMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof deleteAOI>>,
		DeleteAOIMutationVariables
	> = (props) => {
		const { aoiId } = props ?? {};

		return deleteAOI(aoiId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type DeleteAOIMutationResult = NonNullable<
	Awaited<ReturnType<typeof deleteAOI>>
>;

export type DeleteAOIMutationError =
	| DeleteAOI400
	| void
	| DeleteAOI404
	| DeleteAOI500;
export type DeleteAOIMutationVariables = { aoiId: string };

/**
 * @summary Delete an existing Area of Interest
 */
export const useDeleteAOI = <
	TError = DeleteAOI400 | void | DeleteAOI404 | DeleteAOI500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof deleteAOI>>,
			TError,
			DeleteAOIMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof deleteAOI>>,
	TError,
	DeleteAOIMutationVariables,
	TContext
> => {
	return useMutation(getDeleteAOIMutationOptions(options), queryClient);
};
export const getListAOIsUrl = () => {
	return `/aois`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary List all Areas of Interest
 */
export const listAOIs = async (
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest[]> => {
	return customFetch<AreaOfInterest[]>(getListAOIsUrl(), {
		...options,
		method: "GET",
	});
};

export const getListAOIsQueryKey = () => {
	return ["aois"] as const;
};

export const getListAOIsQueryOptions = <
	TData = Awaited<ReturnType<typeof listAOIs>>,
	TError = ListAOIs400 | void | ListAOIs500,
>(options?: {
	query?: Partial<
		UseQueryOptions<Awaited<ReturnType<typeof listAOIs>>, TError, TData>
	>;
	request?: SecondParameter<typeof customFetch>;
}) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getListAOIsQueryKey();

	const queryFn: QueryFunction<Awaited<ReturnType<typeof listAOIs>>> = ({
		signal,
	}) => listAOIs({ signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listAOIs>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAOIsQueryResult = NonNullable<
	Awaited<ReturnType<typeof listAOIs>>
>;
export type ListAOIsQueryError = ListAOIs400 | void | ListAOIs500;

export function useListAOIs<
	TData = Awaited<ReturnType<typeof listAOIs>>,
	TError = ListAOIs400 | void | ListAOIs500,
>(
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listAOIs>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listAOIs>>,
					TError,
					Awaited<ReturnType<typeof listAOIs>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListAOIs<
	TData = Awaited<ReturnType<typeof listAOIs>>,
	TError = ListAOIs400 | void | ListAOIs500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listAOIs>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listAOIs>>,
					TError,
					Awaited<ReturnType<typeof listAOIs>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListAOIs<
	TData = Awaited<ReturnType<typeof listAOIs>>,
	TError = ListAOIs400 | void | ListAOIs500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listAOIs>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List all Areas of Interest
 */

export function useListAOIs<
	TData = Awaited<ReturnType<typeof listAOIs>>,
	TError = ListAOIs400 | void | ListAOIs500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listAOIs>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListAOIsQueryOptions(options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List all Areas of Interest
 */
export const useSetListAOIsQueryData = () => {
	const queryClient = useQueryClient();
	return (
		updater:
			| Awaited<ReturnType<typeof listAOIs>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof listAOIs>> | undefined,
			  ) => Awaited<ReturnType<typeof listAOIs>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof listAOIs>>>(
			{ exact: $exactMatch, queryKey: getListAOIsQueryKey() },
			updater,
		);
	};
};

/**
 * @summary List all Areas of Interest
 */
export const useGetListAOIsQueryData = () => {
	const queryClient = useQueryClient();
	return () =>
		queryClient.getQueryData<Awaited<ReturnType<typeof listAOIs>>>(
			getListAOIsQueryKey(),
		);
};

export const getCreateAOIUrl = () => {
	return `/aois`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Create a new Area of Interest
 */
export const createAOI = async (
	areaOfInterestCreate: AreaOfInterestCreate,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<AreaOfInterest>(getCreateAOIUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(areaOfInterestCreate),
	});
};

export const getCreateAOIMutationKey = () => ["createAOI"] as const;

export const getCreateAOIMutationOptions = <
	TError = CreateAOI400 | void | CreateAOI500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createAOI>>,
		TError,
		CreateAOIMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createAOI>>,
	TError,
	CreateAOIMutationVariables,
	TContext
> => {
	const mutationKey = getCreateAOIMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createAOI>>,
		CreateAOIMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return createAOI(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateAOIMutationResult = NonNullable<
	Awaited<ReturnType<typeof createAOI>>
>;
export type CreateAOIMutationBody = AreaOfInterestCreate;
export type CreateAOIMutationError = CreateAOI400 | void | CreateAOI500;
export type CreateAOIMutationVariables = { data: AreaOfInterestCreate };

/**
 * @summary Create a new Area of Interest
 */
export const useCreateAOI = <
	TError = CreateAOI400 | void | CreateAOI500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createAOI>>,
			TError,
			CreateAOIMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof createAOI>>,
	TError,
	CreateAOIMutationVariables,
	TContext
> => {
	return useMutation(getCreateAOIMutationOptions(options), queryClient);
};
